<template>
  <div class="lever" :class="{ compact }">
    <div class="lever-head">
      <label :for="id" class="lever-label">{{ label }}</label>
      <span class="lever-value">{{ display }}</span>
    </div>
    <div class="track-wrap">
      <input
        :id="id"
        type="range"
        :min="min"
        :max="max"
        :step="step"
        :value="modelValue"
        @input="$emit('update:modelValue', Number($event.target.value))"
      />
      <span v-if="marker !== null" class="marker" :style="{ left: markerLeft }"></span>
    </div>
    <div v-if="!compact" class="lever-scale">
      <span v-if="!markerNearMin">{{ minLabel }}</span><span v-else></span>
      <button
        v-if="marker !== null" type="button" class="marker-btn"
        :style="{ left: markerLeft }" :class="{ 'marker-btn-start': markerNearMin, 'marker-btn-end': markerNearMax }"
        @click="$emit('update:modelValue', marker)"
      >{{ markerLabel }}</button>
      <span v-if="!markerNearMax">{{ maxLabel }}</span><span v-else></span>
    </div>
    <div v-if="note && !compact" class="lever-note">{{ note }}</div>
  </div>
</template>

<script>
export default {
  name: 'LeverSlider',
  props: {
    modelValue: { type: Number, required: true },
    label: { type: String, required: true },
    min: { type: Number, default: 0 },
    max: { type: Number, default: 100 },
    step: { type: Number, default: 0.1 },
    unit: { type: String, default: '%' },
    decimals: { type: Number, default: 1 },
    marker: { type: Number, default: null },
    markerLabel: { type: String, default: 'PROPOSED' },
    note: { type: String, default: '' },
    compact: { type: Boolean, default: false },
  },
  emits: ['update:modelValue'],
  computed: {
    id() {
      return `lever-${this.label.replace(/\W+/g, '-').toLowerCase()}`;
    },
    display() {
      const sign = this.modelValue > 0 ? '+' : '';
      return `${sign}${this.modelValue.toFixed(this.decimals)}${this.unit}`;
    },
    markerPct() {
      return ((this.marker - this.min) / (this.max - this.min)) * 100;
    },
    markerLeft() {
      return `${this.markerPct}%`;
    },
    markerNearMin() {
      return this.marker !== null && this.markerPct < 14;
    },
    markerNearMax() {
      return this.marker !== null && this.markerPct > 86;
    },
    minLabel() {
      return `${this.min}${this.unit}`;
    },
    maxLabel() {
      return `${this.max}${this.unit}`;
    },
  },
};
</script>

<style scoped>
.lever { margin-bottom: 20px; }
.lever.compact { margin-bottom: 8px; }
.lever-head { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 2px; gap: 8px; }
.lever-label { font-size: 13px; font-weight: 600; color: var(--ink); }
.compact .lever-label { font-weight: 400; font-size: 12.5px; }
.lever-value { font-size: 14px; font-weight: 700; color: var(--ink); font-variant-numeric: tabular-nums; white-space: nowrap; }
.compact .lever-value { font-size: 13px; }
.track-wrap { position: relative; }
input[type='range'] {
  width: 100%; margin: 0; -webkit-appearance: none; appearance: none;
  height: 24px; background: transparent; cursor: pointer; display: block;
}
input[type='range']::-webkit-slider-runnable-track { height: 2px; background: var(--ink); }
input[type='range']::-webkit-slider-thumb {
  -webkit-appearance: none; appearance: none; width: 14px; height: 14px; border-radius: 0;
  background: var(--red); margin-top: -6px; border: none;
}
input[type='range']::-moz-range-track { height: 2px; background: var(--ink); }
input[type='range']::-moz-range-thumb { width: 14px; height: 14px; border-radius: 0; background: var(--red); border: none; }
.compact input[type='range']::-webkit-slider-thumb { width: 11px; height: 11px; margin-top: -4.5px; }
.compact input[type='range']::-moz-range-thumb { width: 11px; height: 11px; }
.marker {
  position: absolute; top: 7px; width: 1px; height: 10px; background: var(--ink);
  pointer-events: none;
}
.lever-scale {
  position: relative; display: flex; justify-content: space-between;
  font-size: 9.5px; letter-spacing: 0.06em; color: var(--muted); text-transform: uppercase;
  margin-top: 1px; min-height: 13px;
}
.marker-btn {
  position: absolute; transform: translateX(-50%);
  background: none; border: none; padding: 0; cursor: pointer;
  font-size: 9.5px; letter-spacing: 0.08em; color: var(--muted); text-transform: uppercase;
}
.marker-btn:hover { color: var(--red); }
.marker-btn-start { transform: none; }
.marker-btn-end { transform: translateX(-100%); }
.lever-note { font-size: 10.5px; color: var(--muted); margin-top: 3px; }
</style>
